import { RasterTypes } from '@/modules/Editor/models/raster/rasterTypes'
import { assertImageRaster } from '@/modules/Editor/models/raster/assertImageRaster'
import type { Raster } from '@/modules/Editor/models/raster/Raster'
import type { IPoint } from '@/core/annotations'
import { assertVideoRaster } from '@/modules/Editor/models/raster/assertVideoRaster'
import { getVoxelRasterDimensionsFromMetadata } from '@/modules/Editor/plugins/brush/utils/getVoxelRasterDimensions'
import { assert } from '@/core/utils/assert'
import { getRasterPlaneFromRadiologySlotName } from '@/modules/Editor/utils/raster/getRasterPlaneFromRadiologySlotName'
import { getPointFromScaledPixel } from '@/modules/Editor/plugins/mask/utils/getPointFromScaledPixel'

/**
 * Given a raster and a label index, checks the bounds of the associated mask and returns
 * the central point (centroid) of the mask.
 * For VOXEL rasters, it takes into account scaling.
 */
export const calculateCentroidFromRasterBounds = (
  raster: Raster,
  labelIndex: number,
  frameIndex: number = 0,
): IPoint | null => {
  if (raster.type === RasterTypes.IMAGE) {
    const imageRaster = assertImageRaster(raster)
    if (!labelIndex) {
      throw new Error('No labelIndex found for annotationId')
    }
    const bounds = imageRaster.getBoundsForLabelIndex(labelIndex)
    return {
      x: bounds.topLeft.x + (bounds.bottomRight.x - bounds.topLeft.x) / 2,
      y: bounds.topLeft.y + (bounds.bottomRight.y - bounds.topLeft.y) / 2,
    }
  }

  if (raster.type === RasterTypes.VIDEO || raster.type === RasterTypes.VOXEL) {
    const videoRaster = assertVideoRaster(raster)
    if (!labelIndex) {
      return null
    }

    const labelBounds = videoRaster.getVideoBoundsForLabelIndex(labelIndex)
    const bounds = labelBounds?.[frameIndex]
    if (!bounds) {
      return null
    }

    const center: IPoint = {
      x: bounds.topLeft.x + (bounds.bottomRight.x - bounds.topLeft.x) / 2,
      y: bounds.topLeft.y + (bounds.bottomRight.y - bounds.topLeft.y) / 2,
    }

    if (videoRaster.type === RasterTypes.VIDEO) {
      return center
    }

    // With Voxel we need to account for the pixel scale
    const { metadata, slot_name } = videoRaster.view.fileManager.file
    if (!metadata) {
      return null
    }
    const plane = assert(
      getRasterPlaneFromRadiologySlotName(slot_name, metadata),
      'Could not get the plane from the slot name',
    )
    return getPointFromScaledPixel(
      center,
      metadata,
      getVoxelRasterDimensionsFromMetadata(metadata, plane, videoRaster.view.totalFrames),
    )
  }

  return null
}
